/**
 * A margin percentage as a chip, toned by how it sits against the benchmark.
 *
 * Three tones, not two. A bad chip for every margin under the benchmark put a
 * healthy 36.4% in the same alarm colour as a -18.8% loss, and made the header
 * shout. The chip's dot and text carry the status; the title spells the gap
 * out in words so nothing rides on colour alone.
 */
import { type ChipTone, StatusChip } from "@/components/shared/status-chip";
import { percent } from "@/lib/format";

/** How far a margin sits from the benchmark. Callers format `pts` themselves. */
export function benchmarkGap(
  value: number,
  benchmark: number,
): { pts: number; direction: "below" | "above" } {
  const gap = benchmark - value;
  return { pts: Math.abs(gap), direction: gap > 0 ? "below" : "above" };
}

export function MarginBadge({
  value,
  benchmark,
}: {
  value: number | null;
  benchmark: number | null;
}) {
  // Zero revenue has no margin. Rendering 0% would claim break-even, which is a
  // different statement from "we cannot say".
  if (value === null || value === undefined) {
    return <StatusChip tone="neutral">&mdash;</StatusChip>;
  }

  let tone: ChipTone = "neutral";
  let title: string | undefined;
  if (value < 0) {
    tone = "bad";
  } else if (benchmark !== null && value < benchmark) {
    tone = "warn";
  } else if (benchmark !== null) {
    tone = "good";
  }

  if (benchmark !== null) {
    const { pts, direction } = benchmarkGap(value, benchmark);
    title = `${pts.toFixed(1)} points ${direction} the ${benchmark}% benchmark`;
  }

  return (
    <StatusChip tone={tone} title={title}>
      {percent(value)}
    </StatusChip>
  );
}
